/**
 * Patient documents — PDFs saved in the patient's folder on the server
 * (questionnaires from the tablet, appointment lists), downloadable one by one.
 */

import { useState } from 'react'
import { TRPCClientError } from '@trpc/client'
import { Download, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { trpc } from '@/trpc/client'
import { downloadPdf } from '@/utils/download-pdf'

interface PatientDocumentsProps {
	patientId: string
}

type PatientFile = {
	filename: string
	kind: 'questionnaire' | 'appointments' | 'other'
	appointmentType: string | null
	template: 'classic' | 'modern' | null
	date: string | null
	modifiedAt: string | Date
	size: number
}

// Server messages for these codes are written for staff
const STAFF_FACING_CODES = new Set(['PRECONDITION_FAILED', 'INTERNAL_SERVER_ERROR', 'NOT_FOUND'])

function describeError(err: unknown, fallback: string): string {
	if (err instanceof TRPCClientError && STAFF_FACING_CODES.has(err.data?.code)) {
		return err.message
	}
	return fallback
}

export function PatientDocuments({ patientId }: PatientDocumentsProps) {
	const files = trpc.patient.files.useQuery({ patientId }, { enabled: !!patientId })
	const download = trpc.patient.downloadFile.useMutation()
	const [downloading, setDownloading] = useState<string | null>(null)

	const handleDownload = async (filename: string) => {
		setDownloading(filename)
		try {
			const result = await download.mutateAsync({ patientId, filename })
			downloadPdf(result.pdfBase64, result.filename)
		} catch (err) {
			toast.error(describeError(err, 'Soubor se nepodařilo stáhnout.'))
			// A vanished file means the list is stale
			files.refetch()
		} finally {
			setDownloading(null)
		}
	}

	return (
		<div className="rounded-xl bg-white p-6 shadow-sm border border-[#e8e4df]">
			<h2 className="mb-4 text-lg font-semibold text-[#3d3428]">Dokumenty</h2>

			{files.isLoading ? (
				<div className="flex items-center justify-center py-8">
					<div className="h-6 w-6 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
					<span className="ml-3 text-[#8c8275]">Načítání dokumentů...</span>
				</div>
			) : files.error ? (
				<p className="py-8 text-center text-[#a09689]">
					{describeError(files.error, 'Dokumenty pacienta se nepodařilo načíst.')}
				</p>
			) : !files.data?.length ? (
				<div className="flex flex-col items-center justify-center py-12 text-[#8c8275]">
					<div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#f5ebe0] mb-4">
						<FileText className="h-8 w-8 text-[#d7ad6c] opacity-60" />
					</div>
					<p className="text-[#a09689]">Pacient zatím nemá žádné dokumenty</p>
				</div>
			) : (
				<ul className="space-y-2">
					{files.data.map((file) => (
						<li
							key={file.filename}
							className="flex items-center gap-4 rounded-xl border border-[#e8e4df] px-4 py-3"
						>
							<span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-[#f5ebe0]">
								<FileText className="w-5 h-5 text-[#d7ad6c]" />
							</span>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-2">
									<span className="font-medium text-[#3d3428]">{describeKind(file)}</span>
									{file.template && (
										<Badge variant="outline" className="text-xs text-[#8c8275]">
											{file.template === 'modern' ? 'moderní' : 'klasický'}
										</Badge>
									)}
								</div>
								<p className="truncate text-sm text-[#a09689]">
									{formatDate(file)} · {formatSize(file.size)} · {file.filename}
								</p>
							</div>
							<Button
								variant="outline"
								size="sm"
								disabled={downloading !== null}
								onClick={() => handleDownload(file.filename)}
							>
								<Download className="mr-2 h-4 w-4" />
								{downloading === file.filename ? 'Stahuji…' : 'Stáhnout'}
							</Button>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

function describeKind(file: PatientFile): string {
	switch (file.kind) {
		case 'questionnaire':
			return 'Dotazník'
		case 'appointments':
			return file.appointmentType ? `Objednávky ${file.appointmentType}` : 'Objednávky'
		default:
			return 'Dokument'
	}
}

function formatDate(file: PatientFile): string {
	const date = file.date ? new Date(`${file.date}T00:00:00`) : new Date(file.modifiedAt)
	return date.toLocaleDateString('cs-CZ')
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
